import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';

import BackflowLogFormCommentOnly from './BackflowLogFormCommentOnly';
import BackflowLogFormFull from './BackflowLogFormFull';
import BackflowLogSubmitButtons from './BackflowLogSubmitButtons';
import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';
import OutOfServiceCheckbox from './OutOfServiceCheckbox';

import { BackflowLog, BackflowJobItem } from '../../model';
import axios from '../../../sharedModules/utils/axios.utils';

interface BackflowLogSectionProps {
  backflowJobItem: BackflowJobItem;
  jobStarted: boolean;
  offlineOnly?: boolean;
  fetchBackflowJobItem: () => void;
  onSavedOffline?: () => void;
}

const outOfServiceStatuses = ['out_of_service', 'hidden'];

const BackflowLogSection: React.FC<BackflowLogSectionProps> = ({
  backflowJobItem,
  jobStarted,
  offlineOnly = false,
  fetchBackflowJobItem,
  onSavedOffline,
}) => {
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [formValues, setFormValues] = useState<BackflowLog>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [outOfService, setOutOfService] = useState<boolean>(false);
  const [requiredDeviceImageAttached, setRequiredDeviceImageAttached] = useState(false);
  const [requiredDataplateAttached, setRequiredDataplateAttached] = useState(false);
  const [showRequiredImageErrors, setShowRequiredImageErrors] = useState(false);

  // Load backflow log from locally saved data
  const loadBackflowLogFromLocalStorage = (): boolean => {
    try {
      const jobGroupId = backflowJobItem?.backflow_job_group_id;
      if (!jobGroupId) return false;

      const cacheKey = `backflowJobGroup_${jobGroupId}`;
      const cachedData = localStorage.getItem(cacheKey);

      if (!cachedData) return false;

      const jobGroupData = JSON.parse(cachedData);

      // Find the job item in the cached data
      const item = jobGroupData.jobItems?.find((item: any) => item.id === backflowJobItem?.id);

      // Check if this item has a saved log
      if (item?.backflow_log) {
        // Extract formValues from backflow_log (everything except metadata)
        const { savedAt, needsSync, outOfService: savedOutOfService, ...logFormValues } = item.backflow_log;

        setFormValues(logFormValues || {});
        setOutOfService(savedOutOfService || false);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to load backflow log from localStorage:', error);
      return false;
    }
  };

  const fetchBackflowLog = async () => {
    setLoading(true);
    try {
      const response = await axios.get<any>(`/backflows/backflow_logs/${backflowJobItem.id}`);
      setFormValues(response.data.result || {});
      setErrorMessage('');
    } catch (error: any) {
      // Determine if this is a network error or server error
      const isNetworkError = !navigator.onLine || error.code === 'ERR_NETWORK' || error.message === 'Network Error';

      if (isNetworkError) {
        // Try to load from localStorage
        const loadedFromLocalStorage = loadBackflowLogFromLocalStorage();

        if (loadedFromLocalStorage) {
          setErrorMessage('');
        } else {
          // No saved data, but that's okay - form can start fresh
          setFormValues({});
          setErrorMessage('');
        }
      } else {
        // Server error - show error message
        setErrorMessage('Something went wrong fetching the Backflow Log');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!backflowJobItem?.id) return;

    if (offlineOnly) {
      // Only load from cache, don't fetch from server
      setLoading(true);
      const loaded = loadBackflowLogFromLocalStorage();
      if (!loaded) {
        // No saved data, but that's okay - form can start fresh
        setFormValues({});
      }
      setLoading(false);
    } else {
      // Normal behavior: fetch from server (with cache fallback on error)
      fetchBackflowLog();
    }
  }, [backflowJobItem?.id, offlineOnly]);

  useEffect(() => {
    const updatedServiceStatus = outOfServiceStatuses.includes(backflowJobItem?.status);
    setOutOfService(updatedServiceStatus);
  }, [backflowJobItem?.status]);

  useEffect(() => {
    if (requiredDeviceImageAttached && requiredDataplateAttached) setShowRequiredImageErrors(false);
  }, [requiredDeviceImageAttached, requiredDataplateAttached]);

  const showFullForm = jobStarted && !outOfService;
  const showSubmitButtons = jobStarted || outOfService;

  return (
    <Grid container>
      <LoadingWrapper isLoading={loading}>
        <Grid container>
          <Grid item xs={12}>
            <ErrorMessage message={errorMessage} />
          </Grid>

          <Grid item xs={12}>
            <OutOfServiceCheckbox outOfService={outOfService} setOutOfService={setOutOfService} />
          </Grid>

          {outOfService && (
            <Grid item xs={12}>
              <BackflowLogFormCommentOnly formValues={formValues} setFormValues={setFormValues} />
            </Grid>
          )}

          {showFullForm && (
            <Grid item xs={12}>
              <BackflowLogFormFull
                backflowJobItem={backflowJobItem}
                formValues={formValues}
                setFormValues={setFormValues}
                offlineOnly={offlineOnly}
                onRequiredImagesValidityChange={(deviceImageAttached, dataplateAttached) => {
                  setRequiredDeviceImageAttached(deviceImageAttached);
                  setRequiredDataplateAttached(dataplateAttached);
                }}
                showRequiredImageErrors={showRequiredImageErrors}
              />
            </Grid>
          )}

          {showSubmitButtons && (
            <Grid item xs={12} style={{ marginTop: 15 }}>
              <BackflowLogSubmitButtons
                backflowJobItem={backflowJobItem}
                formValues={formValues}
                outOfService={outOfService}
                fetchBackflowJobItem={fetchBackflowJobItem}
                requiredDeviceImageAttached={showFullForm ? requiredDeviceImageAttached : true}
                requiredDataplateAttached={showFullForm ? requiredDataplateAttached : true}
                onRequiredImagesValidationFailed={() => setShowRequiredImageErrors(true)}
                onSavedOffline={onSavedOffline}
              />
            </Grid>
          )}
        </Grid>
      </LoadingWrapper>
    </Grid>
  );
};

export default BackflowLogSection;
